import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Building2, ExternalLink, Clock } from "lucide-react";
import { EvidenceTag, OutcomeBadge } from "./Badges";
import { SimilarityBars } from "./Similarity";

const tierLabel = {
  official: "Official company info", filing: "Regulatory / public filing",
  report: "Report / research", reporting: "Independent reporting",
  case_study: "Public case study", other: "Other public source",
};

function Metrics({ items }) {
  if (!items || !items.length) return <p className="text-xs text-slate-400">No data available</p>;
  return (
    <div className="space-y-2">
      {items.map((m, i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
          <span className="text-sm text-slate-600">{m.label}</span>
          <span className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-slate-900">{m.value}</span>
            <EvidenceTag type={m.type} />
          </span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{title}</h4>
      {children}
    </div>
  );
}

export function CaseDetailDialog({ open, onClose, c }) {
  if (!c) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="case-detail-dialog" className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 gap-0">
        <DialogTitle className="sr-only">{c.company} — comparable case analysis</DialogTitle>
        <DialogDescription className="sr-only">Detailed before/after analysis, similarity and sources for {c.company}.</DialogDescription>
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white/90 backdrop-blur-md z-10">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-semibold text-slate-900">{c.company}</h3>
                {c.is_demo && (
                  <span className="font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">DEMO DATA</span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{c.industry} • {c.market} • {c.size}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <OutcomeBadge value={c.outcome_category} />
            <span className="flex items-center gap-1 text-sm text-slate-500"><Clock className="w-3.5 h-3.5" /> {c.timeframe}</span>
          </div>

          <Section title="Their decision"><p className="text-sm text-slate-700 leading-relaxed">{c.their_decision}</p></Section>
          <Section title="Why"><p className="text-sm text-slate-700 leading-relaxed">{c.why || "Unknown"}</p></Section>

          <div className="grid sm:grid-cols-2 gap-6">
            <Section title="Before"><Metrics items={c.before} /></Section>
            <Section title="After"><Metrics items={c.after} /></Section>
          </div>

          <Section title="What we learn">
            <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-4">{c.what_we_learn}</p>
          </Section>

          <Section title={`Similarity to your business — overall ${c.similarity?.overall ?? 0}`}>
            <div className="bg-white border border-slate-200 rounded-lg p-4"><SimilarityBars similarity={c.similarity} /></div>
          </Section>

          {c.comparison_table?.length > 0 && (
            <Section title="Their business vs your business">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-2.5 font-medium text-slate-500 text-xs uppercase tracking-wide">Factor</th>
                      <th className="px-4 py-2.5 font-medium text-slate-500 text-xs uppercase tracking-wide">Your business</th>
                      <th className="px-4 py-2.5 font-medium text-slate-500 text-xs uppercase tracking-wide">Comparable business</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.comparison_table.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-4 py-2.5 text-slate-500">{r.factor}</td>
                        <td className="px-4 py-2.5 text-slate-900 font-medium">{r.your_business}</td>
                        <td className="px-4 py-2.5 text-slate-900 font-medium">{r.their_business}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Section title="Sources">
            {c.sources?.length ? (
              <ul className="space-y-2">
                {c.sources.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 shrink-0 mt-0.5">{tierLabel[s.tier] || "Source"}</span>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline flex items-center gap-1">
                        {s.title} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-slate-600">{s.title}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p className="text-xs text-slate-400">No public sources referenced.</p>}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
